import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Lenis from 'lenis';
import Navigation from './components/Navigation';
import ScrollToTop from './components/ScrollToTop';
import WhatsAppButton from './components/WhatsAppButton';
import Home from './pages/Home';
import Footer from './components/Footer';
import { initConfig } from './utils/configClient';
import './App.css';

const Products    = lazy(() => import('./pages/Products'));
const Cart        = lazy(() => import('./pages/Cart'));
const Checkout    = lazy(() => import('./pages/Checkout'));
const WhoAreWe    = lazy(() => import('./pages/WhoAreWe'));
const Blog        = lazy(() => import('./pages/Blog'));
const Contact     = lazy(() => import('./pages/Contact'));
const BlogDetail  = lazy(() => import('./pages/BlogDetail'));
const Traceability  = lazy(() => import('./pages/Traceability'));
const FarmerImpact  = lazy(() => import('./pages/FarmerImpact'));
const Recipes     = lazy(() => import('./pages/Recipes'));
const Nutrition   = lazy(() => import('./pages/Nutrition'));
const Matters     = lazy(() => import('./pages/Matters'));
const Faq         = lazy(() => import('./pages/Faq'));
const RuralReaps     = lazy(() => import('./pages/RuralReaps'));
const PrivacyPolicy  = lazy(() => import('./pages/PrivacyPolicy'));
const NotFound       = lazy(() => import('./pages/NotFound'));

function App() {
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    updateCartCount();

    // Listen for cart updates
    window.addEventListener('cartUpdated', updateCartCount);

    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  useEffect(() => {
    initConfig();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') initConfig();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // ---- Smooth scroll (Lenis) ------------------------------------------
  // Intercepts wheel/keyboard/touch scroll and interpolates it — fast
  // flings no longer teleport to the end of the page. Native scroll
  // semantics are preserved, so position:fixed, IntersectionObserver,
  // and per-frame getBoundingClientRect() (used by HorizontalProducts and
  // the WhyItMatters transition) all continue to work correctly.
  //
  // Enabled on both desktop and touch devices. Only skipped when the user
  // has prefers-reduced-motion set (accessibility).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    const lenis = new Lenis({
      // Duration is how long the eased catch-up takes. Default 1.2s feels
      // premium without being sluggish. Tune here if it needs more/less.
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo-out
      smoothWheel: true,
      // Touch smoothing on. Multiplier tuned down slightly so a swipe
      // doesn't over-shoot compared to native inertial scroll.
      smoothTouch: true,
      touchMultiplier: 1.5,
    });

    // Expose so other components (modals, dialogs) can pause it while open
    // and use lenis.scrollTo() for slow/eased programmatic scrolls.
    window.lenis = lenis;

    let rafId = 0;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      if (window.lenis === lenis) delete window.lenis;
    };
  }, []);

  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const count = cart.reduce((sum, item) => sum + item.quantity, 0);
    setCartCount(count);
  };

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <Navigation cartCount={cartCount} />
          <WhatsAppButton />
          <main className="main-content">
            <Suspense fallback={<div style={{ minHeight: '60vh' }} />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<Products />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/who-are-we" element={<WhoAreWe />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog/:id" element={<BlogDetail />} />
                <Route path="/who-are-we/traceability" element={<Traceability />} />
                <Route path="/who-are-we/farmer-impact" element={<FarmerImpact />} />
                <Route path="/who-are-we/recipes" element={<Recipes />} />
                <Route path="/who-are-we/faq" element={<Faq />} />
                <Route path="/traceability" element={<Traceability />} />
                <Route path="/b2b-enquiry" element={<RuralReaps />} />
                <Route path="/nutrition" element={<Nutrition />} />
                <Route path="/why-it-matters" element={<Matters />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
